import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, Loader2, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { DIAL_CODES } from "./CartModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Special/custom order request: description + name + phone (with mandatory
// international prefix). NO payment — the store contacts the customer on WhatsApp.
export const SpecialOrderModal = ({ isOpen, onClose }) => {
  const { t, lang } = useLanguage();
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [dial, setDial] = useState("+39");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const phoneDigits = phone.replace(/[^0-9]/g, "");
  const phoneValid = /^[0-9]{6,14}$/.test(phoneDigits);

  const close = () => {
    onClose();
    setError("");
    if (done) { setDone(false); setDescription(""); setName(""); setDial("+39"); setPhone(""); }
  };

  const handleSubmit = async () => {
    if (!description.trim()) { setError(t("special.descRequired")); return; }
    if (!name.trim()) { setError(t("cart.required")); return; }
    if (!phoneValid) { setError(t("cart.phoneInvalid")); return; }
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/special-requests`, {
        description: description.trim(),
        customer_name: name.trim(),
        customer_phone: `${dial}${phoneDigits}`,
        lang,
      });
      setDone(true);
    } catch (err) {
      console.error("Special request failed:", err?.response?.data || err.message);
      setError(t("cart.checkoutError"));
    } finally {
      setSending(false);
    }
  };

  const labelCls = "block text-sm md:text-base font-bold tracking-[0.15em] uppercase text-white mb-2 font-body";
  const inputCls = "w-full px-4 py-3.5 bg-transparent border border-white/10 rounded-sm text-white font-body text-base focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-500";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={close}
          data-testid="special-order-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-[#0A0A0A] border-2 border-[#009A49]/60 rounded-sm p-8 max-h-[90vh] overflow-y-auto"
            data-testid="special-order-modal"
          >
            <button onClick={close} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors" data-testid="special-order-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>

            {done ? (
              <div className="text-center py-6" data-testid="special-order-success">
                <CheckCircle2 className="w-14 h-14 text-[#009A49] mx-auto mb-5" />
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white tracking-wide mb-3">
                  {t("special.successTitle")}
                </h3>
                <p className="text-zinc-400 font-body text-base leading-relaxed mb-8">
                  {t("special.successText")}
                </p>
                <button
                  onClick={close}
                  className="btn-emerald px-10 py-3.5 text-sm font-semibold tracking-wider uppercase rounded-sm"
                  data-testid="special-order-done-btn"
                >
                  OK
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-7">
                  <div className="w-11 h-11 rounded-full bg-[#009A49] flex items-center justify-center flex-shrink-0">
                    <Star className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-white tracking-wide">
                    {t("special.title")}
                  </h3>
                </div>

                {/* Free-text description */}
                <div className="mb-5">
                  <label className={labelCls}>{t("special.descLabel")}</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("special.descPh")}
                    rows={4}
                    className={`${inputCls} resize-none`}
                    data-testid="special-desc-input"
                  />
                </div>

                {/* Name */}
                <div className="mb-5">
                  <label className={labelCls}>{t("cart.name")}</label>
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("cart.namePh")}
                    className={inputCls}
                    data-testid="special-name-input"
                  />
                </div>

                {/* Phone with mandatory international prefix */}
                <div className="mb-6">
                  <label className={labelCls}>{t("cart.phone")}</label>
                  <div className="flex gap-3">
                    <select
                      value={dial}
                      onChange={(e) => setDial(e.target.value)}
                      className="px-3 py-3.5 bg-[#0A0A0A] border border-white/10 rounded-sm text-white font-body text-base focus:outline-none focus:border-[#009A49] transition-colors flex-shrink-0"
                      data-testid="special-dial-select"
                      aria-label="Prefisso internazionale"
                    >
                      {DIAL_CODES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <input
                      type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("cart.phonePh")}
                      className={inputCls}
                      data-testid="special-phone-input"
                    />
                  </div>
                </div>

                {error && <p className="text-red-400 font-body text-sm mb-3" data-testid="special-error">{error}</p>}

                <button
                  onClick={handleSubmit}
                  disabled={sending}
                  className="btn-emerald w-full py-4 text-base font-semibold tracking-wider uppercase rounded-sm flex items-center justify-center gap-3 disabled:opacity-90"
                  data-testid="special-submit-btn"
                >
                  {sending ? (<><Loader2 className="w-5 h-5 animate-spin" />{t("cart.processing")}</>) : (<><Star className="w-5 h-5" />{t("special.send")}</>)}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
